import { put, takeLatest } from 'redux-saga/effects';
import { COMMON_ERROR, CURRENT_HASH, VERSION_UPDATE_DATE } from '../../../shared/constants/commonConstants';
import SendRequest from '../../../shared/services/sendRequest.service';
import actions from '../../types/utilTypes';
import { newVersionAvilable, saveNotifications, updateUnreadAndTotalCount } from '../../actions/utilAction';
import { GLOBAL_URL } from '../../../shared/constants/dataServiceUrl';
import { centeredNotification } from '../../../utils/utility';
import { ssoLogin } from '../../actions/authAction';
import { clearLookup, clearPayerLookUp } from '../../actions/lookUpAction';
import actionTypes from '../../types/blogsTypes';
import { setBlogs } from '../../actions/blogsAction';
import BLOGS from '../../../config/ihxDefaultBlogs.json';

function* getAndVerifyHash(): any {
  try {
    const sendRequest = SendRequest.instance;
    const response =
      yield sendRequest.get(`/version.json`,
        { isShowErrorInInterceptor: { isShowErrorInModal: false } },
        { headers: { 'Cache-Control': 'no-cache' }, isShowErrorInInterceptor: { isShowErrorInModal: false } },
      );
    const { hash, date } = response;
    yield localStorage.setItem(VERSION_UPDATE_DATE, date);
    const currentHash = yield localStorage.getItem(CURRENT_HASH);
    if (!currentHash) {
      yield localStorage.setItem(CURRENT_HASH, hash);
    } else {
      if (currentHash !== hash) {
        yield put(newVersionAvilable(true));
      }
    }
  } catch (err) {}
}

function* checkHashSaga(): any {
  yield getAndVerifyHash();
}

function* notifyPayerForJitsiMeet({payload: {payload: request, onSuccess, onError}}: any): any {
  const {meetingLink, payerId, productCode, reason = 'IHX Connect', userEmail} = request;
  if (!payerId) {
    centeredNotification('Please select a payer to connect.');
    return;
  }
  if (!meetingLink) {
    centeredNotification(COMMON_ERROR);
    return;
  }
  const updatedRequest = {
    payerId,
    productCode,
    reason,
    userEmail,
    meetingLink: `https://${process.env.REACT_APP_JITSI_BASE_URL}/${meetingLink}`,
  };
  const url = `${GLOBAL_URL.CRM_BASE_URL}/Provider/SendMeetup`;
  const sendRequest = SendRequest.instance;
  try {
    const { isSuccess, errorMessage } = yield sendRequest.post(url, updatedRequest, { isShowErrorInInterceptor: false });
    if (isSuccess) {
      onSuccess(request);
    } else {
      onError(errorMessage || COMMON_ERROR);
    }
  } catch (e) {
    onError(COMMON_ERROR);
    console.error(e);
  }
}

function* refreshInternalData({ payload: { refreshLogin, refreshLookUp, refreshPayerLookUp, masterDataCurrVersion } }: any): Generator {
  if (process.env.REACT_APP_REFRESH_INTERNAL_DATA_ALLOWED === 'true') {
    if (refreshLogin) {
        yield put(ssoLogin(''));
    }
    if (refreshLookUp) {
      yield put(clearLookup());
      localStorage.removeItem('lookUp');
    }
    if (refreshPayerLookUp) {
      yield put(clearPayerLookUp());
      localStorage.removeItem('payerLookUp');
    }
    if (masterDataCurrVersion) {
      localStorage.setItem('x-md-cv', masterDataCurrVersion);
    }
  }
}

function* fetchBlogs(): Generator {
  try {
    const result = yield fetch(`${GLOBAL_URL.REACT_APP_COMMON_BUCKET_URL}blogs-resources/ihxDefaultBlogs.json`)
      .then((response: any): any => response.json());
    if (result) {
      yield put(setBlogs(result));
    } else {
      yield put(setBlogs(BLOGS));
    }
  } catch (e) {
    yield put(setBlogs(BLOGS));
  }
}

function* fetchNotificationsSaga(payload: any): Generator {
  const url = `${GLOBAL_URL.CRM_BASE_URL}GetProviderNotifications`;
  try {
    payload.payload.providerId = JSON.parse(localStorage.getItem('providerDetail') || '{}')?.providerId;
    const response: any = yield SendRequest.instance.post(url, payload.payload, undefined, undefined, false);
    if (response?.isSuccess) {
      const q = (response?.notificationMessageList || []).map((ele: any): any => {
        return { ...ele, id: response?.notificationMessageList?.notificationMessageId };
      });
      q.sort((a: any, b: any): number => {
        return new Date(b?.messageCreatedOn).getTime() - new Date(a?.messageCreatedOn).getTime();
      });
      const isAppend = payload.payload.startIndex !== 1 || payload.payload.searchText ? true : false;
      yield put(saveNotifications(q, isAppend));
      if (payload.payload.startIndex === 1) {
        yield put(updateUnreadAndTotalCount(response?.totalNotificationUnReadCount, response?.totalNotificationCount));
      }
    }
  } catch (e) {
    console.error(e);
    // centeredNotification('Error occurred while fetching dashboard data, please try again.');
  }
}

function* enableOrDisableNotificationsSaga({ payload: { isNotificationEnabled, onSuccessIsEnabled, onFailureIsEnabled } }: any): Generator {
  const url = `${GLOBAL_URL.CRM_BASE_URL}EnableDisableNotification`;
  const finalPayload = {
    isEnabled: isNotificationEnabled,
    providerId: JSON.parse(localStorage.getItem('providerDetail') || '{}')?.providerId,
  };
  try {
    const response: any = yield SendRequest.instance.post(url, finalPayload, undefined, undefined, false);

    if (response?.isSuccess) {
      onSuccessIsEnabled();
    } else {
      onFailureIsEnabled(response?.errorMessage);
      throw new Error(response.errors[0]);
    }
  } catch (e) {
    console.error(e);
    // centeredNotification('Error occurred while fetching dashboard data, please try again.');
  }
}

function* subscribeNotificationsSaga(payload: any): Generator {
  const url = `${GLOBAL_URL.CRM_BASE_URL}/Firebase/SubscribeNotification`;
  try {
    const response: any = yield SendRequest.instance.post(url, payload.payload);

    if (!response?.isSuccess) {
      throw new Error(response.errors[0]);
    }
  } catch (e) {
    console.error(e);
    // centeredNotification('Error occurred while fetching dashboard data, please try again.');
  }
}

function* unsubscribeNotificationsSaga(payload: any): Generator {
  const url = `${GLOBAL_URL.CRM_BASE_URL}/Firebase/UnsubscribeNotification`;
  try {
    const response: any = yield SendRequest.instance.post(url, payload.payload);

    if (!response?.isSuccess) {
      throw new Error(response.errors[0]);
    }
  } catch (e) {
    console.error(e);
    // centeredNotification('Error occurred while fetching dashboard data, please try again.');
  }
}

function* readNotificationSaga({ payload: { messageIds, onSuccessRead, onFailureRead, isMarkAllAsRead } }: any): Generator {
  const url = `${GLOBAL_URL.CRM_BASE_URL}UpdateNotification`;
  const finalPayload = {
    providerId: JSON.parse(localStorage.getItem('providerDetail') || '{}')?.providerId,
    notificationMessageIdList: !isMarkAllAsRead ? messageIds : [0],
    isMarkAllAsRead,
  };
  try {
    const response: any = yield SendRequest.instance.post(url, finalPayload, undefined, undefined, false);
    const { isSuccess, errors, errorMessage } = response;
    if (!isSuccess) {
      onFailureRead(errorMessage);
      throw new Error(errors[0]);
    } else {
      onSuccessRead();
    }
  } catch (e) {
    console.error(e);
    // centeredNotification('Error occurred while fetching dashboard data, please try again.');
  }
}

export function* watchUtil(): any {
  yield takeLatest(actions.CHECK_HASH, checkHashSaga);
  yield takeLatest(actions.TRIGGER_JITSI_EMAIL, notifyPayerForJitsiMeet);
  yield takeLatest(actions.REFRESH_INTERNAL_DATA, refreshInternalData);
  yield takeLatest(actionTypes.FETCH_BLOGS_SERVER, fetchBlogs);
  yield takeLatest(actions.FETCH_NOTIFICATIONS, fetchNotificationsSaga);
  yield takeLatest(actions.SUBSCRIBE_NOTIFICATIONS, subscribeNotificationsSaga);
  yield takeLatest(actions.UNSUBSCRIBE_NOTIFICATIONS, unsubscribeNotificationsSaga);
  yield takeLatest(actions.READ_NOTIFICATIONS, readNotificationSaga);
  yield takeLatest(actions.ENABLE_DISABLE_NOTIFICATIONS, enableOrDisableNotificationsSaga);
}

export default watchUtil;
